/* eslint-disable react-refresh/only-export-components */
// src/main.tsx
import {
  StrictMode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import "./index.css";
import App from "./App";
import { setupPWA } from "./pwa";
import {
  tenantBaseFrom,
  getTenantFromPath,
} from "./tenant";
import { tokenExpired } from "./context/auth";
import { Button } from "@mui/material";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "@/store/store";
import { initPwaInstallCapture } from "./pwaInstallState";

initPwaInstallCapture();


async function logoutSideEffects() {
  localStorage.removeItem("tokens");
  localStorage.removeItem("user_info");
  sessionStorage.clear();
  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
}

function TenantWatcher({
  onTenantChange,
}: {
  onTenantChange: (tenantBase: string) => void;
}) {
  const location = useLocation();
  const prevTenantRef = useRef<string | null>(
    getTenantFromPath(window.location.pathname)
  );

  useEffect(() => {
    const prevTenant = prevTenantRef.current;
    const currTenant = getTenantFromPath(window.location.pathname);
    if (prevTenant && currTenant && prevTenant !== currTenant) {
      // Build a new tenant base from current location
      const base = tenantBaseFrom(`/${currTenant}`); // yields "/pwa_xxx/<tenant>"
      onTenantChange(base);
    }
    prevTenantRef.current = currTenant;
  }, [location.pathname, onTenantChange]);

  return null;
}

function Boot() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [, setOfflineReady] = useState(false);
  const [blockedForRedirect, setBlockedForRedirect] = useState(false);
  const applyUpdateRef = useRef<null | (() => Promise<void>)>(null);
  const redirectingRef = useRef(false); // guard against double redirects

  useEffect(() => {
    const applyUpdate = setupPWA(
      () => setNeedRefresh(true),
      () => setOfflineReady(true)
    );
    applyUpdateRef.current = applyUpdate;
  }, []);

  // This is the tenant-aware basename the router must use
  const initialTenantBase = useMemo(
    () => tenantBaseFrom(window.location.pathname), // e.g. "/pwa_local/digicall"
    []
  );

  // On first load, if stored tenant differs from current, log out and bounce to /login once
  useEffect(() => {
    (async () => {
      try {
        const raw = localStorage.getItem("tokens");
        if (raw) {
          const tokens = JSON.parse(raw);
          if (tokenExpired(tokens)) {
            setBlockedForRedirect(true);
            await logoutSideEffects();
            window.location.replace(`${initialTenantBase}/login`);
            return; // stop further boot logic
          }
        }
      } catch {
        // if parse fails, treat as unauth
        setBlockedForRedirect(true);
        await logoutSideEffects();
        window.location.replace(`${initialTenantBase}/login`);
        return;
      }
    })();
  }, [initialTenantBase]);

  const handleTenantChange = useCallback(async (tenantBase: string) => {
    // Don’t redirect if we’re already on this base
    if (tenantBase === localStorage.getItem("__tenant_base")) return;
    localStorage.setItem("__tenant_base", tenantBase);
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    await logoutSideEffects();
    // CHANGE: use replace (not href) to avoid stale history entries
    window.location.replace(`${tenantBase}/login`);
  }, []);

  if (blockedForRedirect) return null;

  return (
    <>
      <BrowserRouter basename={import.meta.env.BASE_URL}>
        <TenantWatcher onTenantChange={handleTenantChange} />
        <App />
      </BrowserRouter>

      {needRefresh && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "center",
            padding: "10px 16px",
            background: "#111",
            color: "#fff",
            zIndex: 9999,
          }}
        >
          <span>New version available.</span>
          <Button
            onClick={() => applyUpdateRef.current?.()}
            style={{ padding: "6px 10px", cursor: "pointer" }}
          >
            Reload
          </Button>
        </div>
      )}
    </>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Boot />
      </PersistGate>
    </Provider>
  </StrictMode>,
);

